import React, { useState, useEffect, useContext } from "react";
import { api } from "../../api";
import Ctx from "../../contexts/AppContext";
import { crd, bdg, sp, rad, type, icBox, listRow } from "../../constants/styles";
import Ic from "../common/Ic";

// CSV utilities ───────────────────────────────────────────────
const escCsv = (v) => {
  const s = String(v ?? "");
  return s.includes(",") || s.includes('"') || s.includes("\n") ? `"${s.replace(/"/g, '""')}"` : s;
};
const downloadCsv = (rows, headers, filename) => {
  if (!rows.length) return false;
  const csv = [headers.map(h => h.label).join(","), ...rows.map(r => headers.map(h => escCsv(r[h.key])).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = filename; a.click();
  return true;
};

// Print-friendly HTML opened in a new tab via Blob URL — same pattern
// as the agreement PDF flow. The user gets the browser's "Save as PDF"
// dialog, which is the lightest cross-platform PDF generator.
const downloadPdf = (rows, headers, title, range) => {
  if (!rows.length) return false;
  const escHtml = (v) => String(v ?? "").replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]);
  const tbody = rows.map(r => `<tr>${headers.map(h => `<td>${escHtml(r[h.key])}</td>`).join("")}</tr>`).join("");
  const html = `<!DOCTYPE html><html lang="en"><head>
    <meta charset="UTF-8">
    <title>${escHtml(title)}</title>
    <style>
      @page { margin: 14mm; size: A4 landscape; }
      body { font-family: 'Helvetica', Arial, sans-serif; padding: 0; margin: 0; color: #222; font-size: 11px; }
      h1 { font-size: 16px; margin: 0 0 4px; }
      .meta { color: #666; font-size: 10px; margin-bottom: 12px; }
      table { width: 100%; border-collapse: collapse; }
      th, td { padding: 6px 8px; border: 1px solid #ddd; text-align: left; vertical-align: top; }
      th { background: #f3f4f6; font-weight: 600; }
      tr:nth-child(even) td { background: #fafafa; }
      .actions { margin-bottom: 12px; }
      button { padding: 6px 14px; border-radius: 6px; border: 1px solid #3b82f6; background: #3b82f6; color: #fff; cursor: pointer; font-size: 12px; font-weight: 600; }
      @media print { .actions { display: none; } }
    </style>
  </head><body>
    <div class="actions"><button onclick="window.print()">Print / Save as PDF</button></div>
    <h1>${escHtml(title)}</h1>
    <div class="meta">${escHtml(range)} · ${rows.length} rows · Exported ${new Date().toLocaleString()}</div>
    <table>
      <thead><tr>${headers.map(h => `<th>${escHtml(h.label)}</th>`).join("")}</tr></thead>
      <tbody>${tbody}</tbody>
    </table>
  </body></html>`;
  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const w = window.open(url, "_blank", "noopener,noreferrer");
  if (!w) {
    const a = document.createElement("a"); a.href = url; a.target = "_blank"; a.rel = "noopener noreferrer";
    document.body.appendChild(a); a.click(); a.remove();
  }
  setTimeout(() => URL.revokeObjectURL(url), 60000);
  return true;
};

// Date helpers ────────────────────────────────────────────────
const todayISO = () => new Date().toISOString().slice(0, 10);
const daysAgoISO = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); };

const REPORTS = [
  {
    id: "organizations", icon: "building", color: "#3b82f6", title: "Organizations",
    desc: "All orgs with plan, status, usage counts, revenue",
    headers: [
      { key: "name", label: "Name" }, { key: "slug", label: "Slug" }, { key: "plan", label: "Plan" },
      { key: "active", label: "Active" }, { key: "owner_phone", label: "Owner Phone" },
      { key: "prop_count", label: "Properties" }, { key: "tenant_count", label: "Tenants" },
      { key: "sp_count", label: "Service Persons" }, { key: "pay_count", label: "Payments" },
      { key: "revenue", label: "Revenue" }, { key: "created_at", label: "Created" },
    ]
  },
  {
    id: "properties", icon: "home", color: "#22c55e", title: "All Properties",
    desc: "Every property across all orgs with tenant, rent, type",
    headers: [
      { key: "org_name", label: "Organization" }, { key: "addr", label: "Address" },
      { key: "property_type", label: "Type" }, { key: "rooms", label: "Rooms" },
      { key: "rent", label: "Rent" }, { key: "dep", label: "Deposit" },
      { key: "occ", label: "Occupied" }, { key: "tenant_name", label: "Tenant" },
      { key: "tenant_phone", label: "Tenant Phone" }, { key: "created_at", label: "Created" },
    ]
  },
  {
    id: "tenants", icon: "users", color: "#a855f7", title: "All Tenants",
    desc: "Every tenant across all orgs with property, rent, verification",
    headers: [
      { key: "org_name", label: "Organization" }, { key: "name", label: "Name" },
      { key: "ph", label: "Phone" }, { key: "email", label: "Email" },
      { key: "property_addr", label: "Property" }, { key: "rent", label: "Rent" },
      { key: "id_type", label: "ID Type" }, { key: "verified", label: "Verified" },
      { key: "join_date", label: "Join Date" }, { key: "vac", label: "Vacate" },
    ]
  },
  {
    id: "payments", icon: "credit-card", color: "#f59e0b", title: "All Payments",
    desc: "Every payment across all orgs with status, tenant, amount",
    headers: [
      { key: "org_name", label: "Organization" }, { key: "tenant_name", label: "Tenant" },
      { key: "tenant_phone", label: "Phone" }, { key: "property_addr", label: "Property" },
      { key: "mo", label: "Month" }, { key: "amt", label: "Rent" }, { key: "com", label: "Common" },
      { key: "st", label: "Status" }, { key: "mth", label: "Method" }, { key: "dt", label: "Date" },
    ]
  },
  {
    id: "billing", icon: "dollar", color: "#22c55e", title: "Billing Transactions",
    desc: "All Razorpay transactions with org, amount, status",
    headers: [
      { key: "org_name", label: "Organization" }, { key: "amount", label: "Amount (paise)" },
      { key: "plan_type", label: "Plan" }, { key: "status", label: "Status" },
      { key: "razorpay_payment_id", label: "Razorpay ID" }, { key: "created_at", label: "Date" },
    ]
  },
];

// Preset date ranges shown as pill buttons. "All time" sends no
// date params so older rows remain visible — useful when admin
// just wants the full export they had before.
const RANGES = [
  { id: "all", label: "All time" },
  { id: "7d",  label: "Last 7 days",  from: () => daysAgoISO(7),  to: () => todayISO() },
  { id: "30d", label: "Last 30 days", from: () => daysAgoISO(30), to: () => todayISO() },
  { id: "90d", label: "Last 90 days", from: () => daysAgoISO(90), to: () => todayISO() },
  { id: "custom", label: "Custom" },
];

export default function AdminReports() {
  const { h, go, flash } = useContext(Ctx);
  const [stats, setStats] = useState(null);
  const [activeReport, setActiveReport] = useState(null); // null = list view
  const [rangeId, setRangeId] = useState("all");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [previewRows, setPreviewRows] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  // Plan filter — only meaningful for the Organizations + Billing
  // reports (the only two that carry a plan column). Pulled from
  // /admin/plans so newly-created plans show automatically without a
  // code change. Defaults to "all" so existing report behaviour is
  // preserved when admin doesn't touch the dropdown.
  const [planFilter, setPlanFilter] = useState("all");
  const [availablePlans, setAvailablePlans] = useState([]);

  useEffect(() => {
    api.adminGetStats().then(r => { if (r.success) setStats(r.stats); });
    api.adminGetPlans().then(r => { if (r.success) setAvailablePlans(r.plans || []); }).catch(() => {});
  }, []);

  // Resolved date range from the selected preset / custom inputs.
  const effectiveRange = (() => {
    if (rangeId === "all") return { from: "", to: "" };
    if (rangeId === "custom") return { from: customFrom, to: customTo };
    const r = RANGES.find(x => x.id === rangeId);
    return { from: r?.from?.() || "", to: r?.to?.() || "" };
  })();
  const rangeLabel = (() => {
    let base;
    if (rangeId === "all") base = "All time";
    else {
      const er = effectiveRange;
      if (er.from && er.to) base = `${er.from} → ${er.to}`;
      else if (er.from) base = `From ${er.from}`;
      else if (er.to) base = `Until ${er.to}`;
      else base = "—";
    }
    // Include plan filter in the label so the PDF / on-screen
    // summary lists exactly what was filtered.
    if (planFilter && planFilter !== "all") {
      const planName = (availablePlans.find(p => p.id === planFilter) || {}).name || planFilter;
      return `${base} · ${planName} plan`;
    }
    return base;
  })();

  // Plan filter only applies to reports that have a plan column.
  const planFilterApplies = activeReport && (activeReport.id === "organizations" || activeReport.id === "billing");

  // Fetch the preview rows whenever the active report, range, or plan
  // filter changes. Plan filter is silently dropped for reports that
  // don't support it (server ignores the param either way).
  useEffect(() => {
    if (!activeReport) return;
    const er = effectiveRange;
    setPreviewLoading(true);
    setPreviewRows([]);
    const params = { from: er.from, to: er.to };
    if (planFilterApplies) params.plan = planFilter;
    api.adminReport(activeReport.id, params).then(res => {
      if (res.success) setPreviewRows(res.data || []);
      else flash(res.message || "Failed to load report", "error");
    }).catch(() => flash("Failed to load report", "error"))
      .finally(() => setPreviewLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeReport?.id, rangeId, customFrom, customTo, planFilter]);

  const tabS = (active) => ({
    padding: `${sp.xs}px ${sp.md}px`, borderRadius: rad.pill, border: "none",
    background: active ? h.ac : "transparent", color: active ? "#fff" : h.ts,
    fontSize: 11, fontWeight: active ? 600 : 400, cursor: "pointer",
  });

  // Cap rendered preview rows so a 10K-row payments dump doesn't
  // freeze the page — admin still gets full data on download.
  const PREVIEW_CAP = 50;

  // ─── PREVIEW SCREEN ─────────────────────────────────────────
  if (activeReport) {
    const r = activeReport;
    const visible = previewRows.slice(0, PREVIEW_CAP);
    const truncated = previewRows.length > PREVIEW_CAP;
    const filename = (ext) => {
      const datePart = effectiveRange.from || effectiveRange.to ? `_${(effectiveRange.from || "").replace(/-/g, '')}-${(effectiveRange.to || "").replace(/-/g, '')}` : "";
      return `platform_${r.id}${datePart}_${todayISO()}.${ext}`;
    };
    return <div style={{ paddingTop: sp.lg }} className="animate-fadeIn">
      <button onClick={() => setActiveReport(null)} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", ...type.sm, marginBottom: sp.md, display: "flex", alignItems: "center", gap: sp.xs }}>
        <Ic n="arrow-left" s={14} c={h.ac} /> Back to Reports
      </button>

      <div style={{ display: "flex", alignItems: "center", gap: sp.md, marginBottom: sp.lg }}>
        <div style={icBox(r.color, 40)}>
          <Ic n={r.icon} s={18} c={r.color} />
        </div>
        <div style={{ flex: 1 }}>
          <h2 style={{ ...type.h2, color: h.tx, margin: 0 }}>{r.title}</h2>
          <div style={{ ...type.xs, color: h.tt, marginTop: 2 }}>{r.desc}</div>
        </div>
      </div>

      {/* Date range presets */}
      <div style={{ display: "flex", gap: sp.xs, marginBottom: sp.sm, flexWrap: "wrap" }}>
        {RANGES.map(rg => <button key={rg.id} onClick={() => setRangeId(rg.id)} style={tabS(rangeId === rg.id)}>
          {rg.label}
        </button>)}
      </div>

      {/* Plan filter — only for reports that carry a plan column.
          Options come from /admin/plans so newly created plans are
          included automatically. */}
      {planFilterApplies && <div style={{ marginBottom: sp.md }}>
        <label style={{ ...type.xs, color: h.ts, display: "block", marginBottom: 4 }}>Plan</label>
        <select
          value={planFilter}
          onChange={e => setPlanFilter(e.target.value)}
          style={{ width: "100%", padding: `${sp.sm}px ${sp.md}px`, borderRadius: rad.sm, border: `1px solid ${h.bd}`, background: h.ib, color: h.tx, fontSize: 13, boxSizing: "border-box" }}
        >
          <option value="all">All plans</option>
          {availablePlans.map(p => (
            <option key={p.id} value={p.id}>{p.name || p.id}{p.is_active === 0 ? " (inactive)" : ""}</option>
          ))}
        </select>
      </div>}

      {/* Custom date inputs (only visible when range = custom) */}
      {rangeId === "custom" && <div style={{ display: "flex", gap: sp.sm, marginBottom: sp.md, flexWrap: "wrap" }}>
        <div style={{ flex: "1 1 140px" }}>
          <label style={{ ...type.xs, color: h.ts, display: "block", marginBottom: 4 }}>From</label>
          <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
            style={{ width: "100%", padding: `${sp.sm}px ${sp.md}px`, borderRadius: rad.sm, border: `1px solid ${h.bd}`, background: h.ib, color: h.tx, fontSize: 13, boxSizing: "border-box" }} />
        </div>
        <div style={{ flex: "1 1 140px" }}>
          <label style={{ ...type.xs, color: h.ts, display: "block", marginBottom: 4 }}>To</label>
          <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)}
            style={{ width: "100%", padding: `${sp.sm}px ${sp.md}px`, borderRadius: rad.sm, border: `1px solid ${h.bd}`, background: h.ib, color: h.tx, fontSize: 13, boxSizing: "border-box" }} />
        </div>
      </div>}

      {/* Summary line + download buttons */}
      <div style={{ ...crd(h), display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: sp.md, flexWrap: "wrap", gap: sp.sm }}>
        <div>
          <div style={{ ...type.sm, color: h.tx, fontWeight: 600 }}>{previewRows.length} rows</div>
          <div style={{ ...type.xs, color: h.tt, marginTop: 2 }}>{rangeLabel}</div>
        </div>
        <div style={{ display: "flex", gap: sp.xs }}>
          <button
            disabled={!previewRows.length}
            onClick={() => downloadCsv(previewRows, r.headers, filename("csv")) ? flash(`${r.title} exported (${previewRows.length} rows)`) : flash("No data", "error")}
            style={{ padding: `${sp.xs + 2}px ${sp.md}px`, borderRadius: rad.sm, border: "none", background: previewRows.length ? r.color : h.bd, color: "#fff", fontSize: 12, fontWeight: 600, cursor: previewRows.length ? "pointer" : "not-allowed", opacity: previewRows.length ? 1 : 0.5 }}
          >
            Download CSV
          </button>
          <button
            disabled={!previewRows.length}
            onClick={() => downloadPdf(previewRows, r.headers, r.title, rangeLabel) || flash("No data", "error")}
            style={{ padding: `${sp.xs + 2}px ${sp.md}px`, borderRadius: rad.sm, border: `1px solid ${h.bd}`, background: "transparent", color: h.tx, fontSize: 12, fontWeight: 600, cursor: previewRows.length ? "pointer" : "not-allowed", opacity: previewRows.length ? 1 : 0.5 }}
          >
            Download PDF
          </button>
        </div>
      </div>

      {/* Preview table */}
      {previewLoading ? <div style={{ textAlign: "center", padding: sp.xxxl }}>
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={h.ac} strokeWidth="2" style={{ animation: "spin 1s linear infinite" }}><path d="M21 12a9 9 0 11-6.219-8.56" /></svg>
      </div> : previewRows.length === 0 ? <div style={{ ...crd(h), textAlign: "center", padding: sp.xxxl }}>
        <div style={{ ...type.sm, color: h.tt }}>No rows in this range</div>
      </div> : <div style={{ ...crd(h), padding: 0, overflow: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
          <thead>
            <tr style={{ background: h.ib }}>
              {r.headers.map(hd => <th key={hd.key} style={{ padding: `${sp.sm}px ${sp.md}px`, textAlign: "left", color: h.ts, fontWeight: 600, borderBottom: `1px solid ${h.bd}`, whiteSpace: "nowrap" }}>{hd.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {visible.map((row, i) => <tr key={i} style={{ borderBottom: `1px solid ${h.bd}` }}>
              {r.headers.map(hd => <td key={hd.key} style={{ padding: `${sp.sm}px ${sp.md}px`, color: h.tx, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 200 }} title={String(row[hd.key] ?? "")}>{String(row[hd.key] ?? "")}</td>)}
            </tr>)}
          </tbody>
        </table>
        {truncated && <div style={{ padding: sp.md, textAlign: "center", color: h.tt, ...type.xs }}>
          Showing first {PREVIEW_CAP} of {previewRows.length} rows · download for full data
        </div>}
      </div>}
    </div>;
  }

  // ─── LIST SCREEN ────────────────────────────────────────────
  return <div style={{ paddingTop: sp.lg }} className="animate-fadeIn">
    <button onClick={() => go("dash")} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", ...type.sm, marginBottom: sp.md, display: "flex", alignItems: "center", gap: sp.xs }}>
      <Ic n="arrow-left" s={14} c={h.ac} /> Back to Dashboard
    </button>

    <h2 style={{ ...type.h1, color: h.tx, margin: `0 0 ${sp.sm}px` }}>Platform Reports</h2>
    <p style={{ ...type.sm, color: h.ts, margin: `0 0 ${sp.xl}px` }}>Pick a date range, preview the data, then download as CSV or PDF</p>

    {stats && <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: sp.sm, marginBottom: sp.xl }}>
      {[
        { label: "Total Revenue", value: `₹${Math.round((stats.totalRevenue || 0) / 100).toLocaleString()}`, color: h.gn },
        { label: "This Month", value: `₹${Math.round((stats.monthRevenue || 0) / 100).toLocaleString()}`, color: h.ac },
        { label: "Organizations", value: stats.totalOrgs, color: h.pp || h.ac },
      ].map((s, i) => (
        <div key={i} style={{ ...crd(h), padding: sp.md, textAlign: "center" }}>
          <div style={{ ...type.h3, color: s.color }}>{s.value}</div>
          <div style={{ fontSize: 9, color: h.tt, marginTop: 2 }}>{s.label}</div>
        </div>
      ))}
    </div>}

    <div style={{ ...crd(h), padding: 0, overflow: "hidden" }}>
      {REPORTS.map((r, i) => (
        <div key={r.id} onClick={() => { setActiveReport(r); setRangeId("all"); }}
          style={{
            display: "flex", alignItems: "center", gap: sp.md,
            padding: `${sp.md}px ${sp.lg}px`, cursor: "pointer",
            borderBottom: i < REPORTS.length - 1 ? `1px solid ${h.bd}` : "none",
          }}>
          <div style={icBox(r.color, 36)}>
            <Ic n={r.icon} s={16} c={r.color} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ ...type.sm, color: h.tx, fontWeight: 600 }}>{r.title}</div>
            <div style={{ ...type.xs, color: h.tt }}>{r.desc}</div>
          </div>
          <Ic n="chevron-right" s={14} c={h.tt} />
        </div>
      ))}
    </div>

    {stats && <div style={{ ...crd(h), marginTop: sp.xl }}>
      <div style={{ ...type.cap, color: h.ts, marginBottom: sp.md }}>Platform Overview</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: sp.sm }}>
        {[
          { label: "Properties", value: stats.totalProps },
          { label: "Tenants", value: stats.totalTenants },
          { label: "Payments", value: stats.totalPayments },
          { label: "Service Persons", value: stats.totalServicePersons },
          { label: "Leads", value: stats.totalLeads },
          { label: "Leases", value: stats.totalLeases },
        ].map((s, i) => (
          <div key={i} style={{ textAlign: "center", padding: sp.sm }}>
            <div style={{ ...type.h3, color: h.tx }}>{s.value}</div>
            <div style={{ fontSize: 9, color: h.tt }}>{s.label}</div>
          </div>
        ))}
      </div>
    </div>}
  </div>;
}
